import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import cogoToast from "cogo-toast";
import Layout from "../../../layout";
import {
  getProHunterTrialStatus,
  createProHunterTrialSetupIntent,
  activateProHunterTrial,
} from "../../../redux/actions/jobSeekerSubscriptionActions";
import {
  trackEvent,
  trackSubscriptionActivated,
  trackPaymentInitiated,
} from "../../../utils/analytics";
import "./index.scss";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || process.env.STRIPE_PUBLIC_KEY || ""
);

const StripeSetupForm = ({ onActivated }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        cogoToast.error(submitError.message);
        return;
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/pro-hunter-trial`,
        },
      });

      if (error) {
        cogoToast.error(error.message || "Could not save your card.");
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;

      const result = await activateProHunterTrial({
        payment_method_id: paymentMethodId,
      });
      trackEvent("trial_activate_success", "payment", {
        payment_rail: "stripe",
      });
      trackSubscriptionActivated({
        plan_tier: "pro_hunter",
        payment_rail: "stripe",
        type: "trial_activation",
      });
      onActivated(result);
    } catch (err) {
      cogoToast.error(
        err?.response?.data?.message || "Could not start your trial."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="ph-trial-form">
      <PaymentElement />
      <button
        type="submit"
        className="ph-trial-primary"
        disabled={!stripe || submitting}
      >
        {submitting ? "Starting trial..." : "Save card and start free trial"}
      </button>
      <p className="ph-trial-note">You will not be charged today.</p>
    </form>
  );
};

const ProHunterTrial = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [activating, setActivating] = useState(false);

  const goToMatchScore = () => {
    history.replace("/job-match-score?trial=activated");
  };

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      trackPaymentInitiated(null, "pro_hunter_trial", { source: "pro_hunter_trial_page" });
      try {
        const data = await getProHunterTrialStatus();
        if (cancelled) return;
        setStatus(data);

        if (data?.trial_active) {
          history.replace("/job-match-score?trial=activated");
          return;
        }

        if (!data?.eligible) {
          setLoading(false);
          return;
        }

        if (data.payment_rail === "stripe" && data.has_payment_method) {
          setActivating(true);
          try {
            await activateProHunterTrial({});
            trackEvent("trial_activate_success", "payment", {
              payment_rail: "stripe",
            });
            trackSubscriptionActivated({
              plan_tier: "pro_hunter",
              payment_rail: "stripe",
              type: "trial_activation",
            });
            history.replace("/job-match-score?trial=activated");
          } catch (err) {
            cogoToast.error(
              err?.response?.data?.message || "Could not start your trial."
            );
          } finally {
            setActivating(false);
          }
          return;
        }

        if (data.payment_rail === "stripe" && !data.has_payment_method) {
          const setup = await createProHunterTrialSetupIntent();
          if (!cancelled) setClientSecret(setup.client_secret);
        }
      } catch (err) {
        cogoToast.error("Unable to load trial details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <div className="ph-trial-page">
        <h1>Start your 30-day ProHunter trial</h1>
        <p className="ph-trial-lead">
          Unlimited match scores, ATS insights, AI rewrites, and recruiter
          insights. You will not be charged during the trial.
        </p>

        {loading && <p>{activating ? "Starting trial..." : "Loading..."}</p>}

        {!loading && status && !status.eligible && !status.trial_active && (
          <p>
            {status.payment_rail === "jarapay"
              ? "The ProHunter free trial is available for card billing only, so it is not offered on JaraPay (Nigeria) accounts. You can still subscribe to ProHunter from My Subscriptions."
              : "This trial is not available on your account."}{" "}
            <button
              type="button"
              className="ph-trial-link"
              onClick={() => history.push("/my-subscriptions")}
            >
              View plans
            </button>
          </p>
        )}

        {!loading &&
          status?.eligible &&
          status.payment_rail === "stripe" &&
          clientSecret && (
            <div className="ph-trial-card">
              <p>
                Add a card to start your free trial. You will not be charged
                today. After 30 days you can subscribe or return to the free
                plan.
              </p>
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance: { theme: "stripe" } }}
              >
                <StripeSetupForm onActivated={goToMatchScore} />
              </Elements>
            </div>
          )}
      </div>
    </Layout>
  );
};

export default ProHunterTrial;
